/**
 * Theme configuration file
 * Contains design tokens for colors, spacing, typography, and other UI elements
 *
 * Every brand-derived value comes from `brand.ts`, which mirrors the Shopify webshop. Do not
 * hardcode colors, fonts, radii or shadows here — change `brand.ts` instead, and the whole app
 * follows. What lives here is the mapping from the webshop's vocabulary to this app's tokens,
 * plus the structural tokens (spacing, breakpoints) the webshop has no opinion on.
 */

import { brand } from './brand';

/**
 * Neutral ramp, composited from the webshop's foreground over its background.
 *
 * These are deliberately opaque hex rather than rgba: the app paints muted text on both light
 * pages and dark panels, and a translucent value would disappear on the dark ones.
 */
const neutral = {
  /** foreground @ 10% */
  faint: '#e3e4e5',
  /** foreground @ 15% */
  line: '#d6d8d9',
  /** foreground @ 45% */
  medium: '#8c8f93',
  /** foreground @ 65% */
  dark: '#5a5e64',
  /** foreground @ 75% — the webshop's body text color */
  body: '#40464c',
};

export const theme = {
  colors: {
    /** Primary actions and links. The webshop's buttons are near-black, not blue. */
    primary: brand.scheme.base.button,
    /** Header, footer and the app's dark panels. */
    secondary: brand.scheme.chrome.background,
    /**
     * Selected/active affordances in the dense buylist tables. The webshop has no red accent,
     * so this uses Dawn's own contrast blue.
     */
    accent: brand.scheme.dark.contrast,

    // Semantic colors. The webshop has no equivalent, so these stay local to this app —
    // a buylist is transactional and success/danger must remain unambiguous.
    success: '#2db86b',
    warning: '#f1c40f',
    danger: '#e74c3c',

    text: brand.scheme.base.foreground,
    lightText: neutral.body,
    background: brand.scheme.base.background,
    white: '#ffffff',
    black: '#000000',
    border: neutral.line,
    muted: neutral.body,
    gray: {
      lightest: brand.scheme.base.background,
      light: neutral.faint,
      medium: neutral.medium,
      dark: neutral.dark,
      darkest: brand.scheme.base.foreground,
    },
  },
  fontFamily: brand.typography.bodyFamily,
  /** Headings use a second family on the webshop. */
  fontFamilyHeading: brand.typography.headingFamily,
  fontSizes: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    md: '1rem',       // 16px
    lg: '1.25rem',    // 20px
    xl: '1.5rem',     // 24px
    xxl: '2rem',      // 32px
    xxxl: '2.5rem',   // 40px
  },
  fontWeights: {
    regular: brand.typography.bodyWeight,
    /** The webshop's heading weight. */
    medium: brand.typography.headingWeight,
    semibold: 600,
    bold: brand.typography.bodyWeightBold,
  },
  spacing: {
    xs: '0.5rem',     // 8px
    sm: '1rem',       // 16px
    md: '1.5rem',     // 24px
    lg: '2rem',       // 32px
    xl: '3rem',       // 48px
    xxl: '4rem',      // 64px
    xxxl: '5rem',     // 80px
  },
  /**
   * The webshop is square-cornered. `sm`/`md`/`lg` all resolve to 0 so every existing consumer
   * flattens without being touched; `full` is kept for badges and pills, which stay round.
   */
  borderRadius: {
    sm: brand.shape.card,
    md: brand.shape.card,
    lg: brand.shape.card,
    full: '9999px',
  },
  /**
   * The webshop sets every shadow opacity to 0. Definition comes from borders instead, so these
   * all resolve to `none` and flatten the app in one place.
   */
  shadows: {
    subtle: brand.elevation.card,
    medium: brand.elevation.card,
    strong: brand.elevation.popup,
    card: brand.elevation.card,
    cardHover: brand.elevation.cardHover,
  },
  breakpoints: {
    mobile: '576px',
    tablet: '768px',
    desktop: '1024px',
    widescreen: '1200px',
    wide: '1400px'
  },
  transitions: {
    fast: `${brand.motion.default} ease`,
    normal: `${brand.motion.medium} ease`,
    slow: `${brand.motion.long} ease`,
  }
};

export type Theme = typeof theme;

// Helper function for responsive design
export const media = {
  mobile: `@media (max-width: ${theme.breakpoints.mobile})`,
  tablet: `@media (max-width: ${theme.breakpoints.tablet})`,
  desktop: `@media (max-width: ${theme.breakpoints.desktop})`,
  widescreen: `@media (min-width: ${theme.breakpoints.widescreen})`,
  wide: `@media (min-width: ${theme.breakpoints.wide})`,
};

/**
 * Surfaces that sit on the webshop's dark chrome. Kept here so the white-alpha overlays scattered
 * across the app have one place to come from.
 */
export const onDark = {
  text: brand.scheme.chrome.foreground,
  /** The webshop inverts buttons on its dark chrome: light fill, near-black label. */
  button: brand.scheme.chrome.button,
  buttonText: brand.scheme.chrome.buttonText,
  mutedText: 'rgba(255, 255, 255, 0.75)',
  faintText: 'rgba(255, 255, 255, 0.55)',
  border: 'rgba(255, 255, 255, 0.15)',
  surface: 'rgba(255, 255, 255, 0.06)',
  surfaceHover: 'rgba(255, 255, 255, 0.12)',
};
